<template>
  <div class="container">
    <div class="row">
      <div class="col-12">
        <div class="box mt-2">
          <div class="heading">
            <h3>{{ $t('postalCodes.title') }}</h3>
            <button class="btn btn-primary" @click="openImportModal">{{ $t('postalCodes.csvImport') }}</button>
          </div>

          <!-- Pagination controls -->
          <div v-if="page" class="d-flex justify-content-between align-items-center mt-3 mb-2">
            <div class="d-flex align-items-center">
              <label class="me-2">{{ $t('pagination.itemsPerPage') }}</label>
              <select v-model="filter.limit" @change="changePage" class="form-select form-select-sm" style="width: auto;">
                <option :value="25">{{ $t('pagination.per_page.25') }}</option>
                <option :value="50">{{ $t('pagination.per_page.50') }}</option>
                <option :value="100">{{ $t('pagination.per_page.100') }}</option>
              </select>
            </div>
            <div class="text-muted">
              {{ $t('pagination.showing', { total: page.total, from: page.from, to: page.to }) }}
            </div>
          </div>

          <table v-if="page" class="table table-sm table-list table-hover mt-2">
            <thead>
            <tr>
              <th>{{ $t('postalCodes.table.postalCode') }}</th>
              <th>{{ $t('postalCodes.table.prefecture') }}</th>
              <th>{{ $t('postalCodes.table.city') }}</th>
              <th>{{ $t('postalCodes.table.area') }}</th>
              <th>{{ $t('postalCodes.table.prefectureKana') }}</th>
              <th>{{ $t('postalCodes.table.cityKana') }}</th>
              <th>{{ $t('postalCodes.table.areaKana') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="postalCode in page.items" :key="postalCode.id">
              <td>{{ postalCode.postalCode }}</td>
              <td>{{ postalCode.prefecture }}</td>
              <td>{{ postalCode.city }}</td>
              <td>{{ postalCode.area }}</td>
              <td>{{ postalCode.prefectureKana }}</td>
              <td>{{ postalCode.cityKana }}</td>
              <td>{{ postalCode.areaKana }}</td>
            </tr>
            </tbody>
          </table>
          <Pagination v-if="page" :page="page" class="mt-3" v-model="filter" @change="changePage"/>
        </div>
      </div>
    </div>
  </div>

  <!-- Import Modal -->
  <Modal name="importModal" modal-size="modal-lg">
    <template #header>
      <h5 class="modal-title">{{ $t('postalCodes.importModal.title') }}</h5>
    </template>

    <div class="mb-3">
      <label for="csvFile" class="form-label">{{ $t('postalCodes.importModal.fileLabel') }}</label>
      <input
        type="file"
        class="form-control"
        id="csvFile"
        accept=".csv,.txt"
        @change="onFileSelected"
        ref="fileInput"
      >
      <div class="form-text">
        <a :href=urlCsvPostalCodes target="_blank" class="help-link">
          {{ $t('postalCodes.importModal.fileHelp') }}
        </a>
      </div>
    </div>
    <div v-if="importing" class="alert alert-info">
      {{ $t('postalCodes.importing') }}
    </div>
    <div v-if="importResult" class="alert" :class="importResult.errors.length > 0 ? 'alert-warning' : 'alert-success'">
      <strong>{{ $t('postalCodes.importComplete') }}</strong><br>
      <div>
        {{ $t('postalCodes.importCount', { count: importResult.imported }) }}
      </div>
      <div v-if="importResult.errors.length > 0" class="mt-2">
        <strong>{{ $t('postalCodes.errors') }}</strong>
        <ul class="mb-0">
          <li v-for="(error, index) in importResult.errors.slice(0, 10)" :key="index">{{ error }}</li>
          <li v-if="importResult.errors.length > 10">{{ $t('postalCodes.moreErrors', { count: importResult.errors.length - 10 }) }}</li>
        </ul>
      </div>
    </div>

    <template #footer>
      <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('postalCodes.importModal.cancel') }}</button>
      <button
        type="button"
        class="btn btn-primary"
        @click="handleImport"
        :disabled="!selectedFile || importing"
      >
        {{ $t('postalCodes.importModal.import') }}
      </button>
    </template>
  </Modal>
</template>

<script setup>
import {ref} from 'vue';
import usePagination from '../../../composables/usePagination';
import useModal from '../../../composables/useModal';
import useFlashMessage from '../../../composables/useFlashMessage';
import Pagination from '../../../components/Pagination.vue';
import Modal from '../../../components/Modal.vue';
import PostalCodeFilter from '../../../filters/admin/PostalCodeFilter';
import {importPostalCodes} from '../../../api/admin/PostalCodeApi';

const filter = ref(new PostalCodeFilter());

const {page, fetch, changePage} = usePagination(filter.value);

const {openModal: openImportModal} = useModal('importModal');
const {successMessage, errorMessage} = useFlashMessage();

const selectedFile = ref(null);
const importing = ref(false);
const importResult = ref(null);
const fileInput = ref(null);

const urlCsvPostalCodes = "https://www.post.japanpost.jp/zipcode/dl/kogaki-zip.html"

/**
 * Handle file selection
 */
const onFileSelected = (event) => {
  selectedFile.value = event.target.files[0];
  importResult.value = null;
};

/**
 * Handle CSV import
 */
const handleImport = async () => {
  if (!selectedFile.value) {
    return;
  }

  importing.value = true;
  importResult.value = null;

  try {
    const result = await importPostalCodes(selectedFile.value);
    importResult.value = result;

    if (result.errors.length === 0) {
      successMessage('CSVファイルのインポートが完了しました。');
    } else {
      errorMessage('CSVファイルのインポートが完了しましたが、一部エラーがありました。');
    }

    // Refresh the list
    await fetch();

    // Reset file input
    selectedFile.value = null;
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  } catch (error) {
    console.error('Import error:', error);
    errorMessage('インポート中にエラーが発生しました: ' + (error.message || '不明なエラー'));
  } finally {
    importing.value = false;
  }
};
</script>

<style scoped>
.heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-list {
  font-size: 0.9rem;
}

.table-list th {
  background-color: #f8f9fa;
  font-weight: 600;
  white-space: nowrap;
}

.table-list td {
  vertical-align: middle;
}

.help-link {
  color: #306FC9;
  text-decoration: underline;
}
</style>
